import { InfoChunky } from '@eventbrite/eds-iconography';
import { Illustration } from '@eventbrite/eds-illustration';
import { logEvent } from '@eventbrite/statsig';
import classNames from 'classnames';
import React, { FunctionComponent, useState } from 'react';
import { SHOW_MORE_INFO_BUTTON_LABEL } from '../../../constants';
import { STATSIG_EVENT_NAMES } from '../../../constants/statsig';
import { TicketCommonProps } from '../constants';
import type { TicketQuantityPickerProps } from '../TicketQuantityPicker';
import TicketRemainingTicketsCount from '../TicketRemainingTicketsCount';
import type { TicketSalesEndDateProps } from '../TicketSalesEndDate';
import TicketSalesEndDate from '../TicketSalesEndDate';
import TicketStepperSelector from '../TicketStepperSelector';
import './TicketDisplayCardContentCompactSize.scss';

export type TicketDisplayCardContentCompactSizeProps = Pick<
    TicketCommonProps,
    | 'description'
    | 'displayPrice'
    | 'feeAndTax'
    | 'id'
    | 'isDisabled'
    | 'quantityRemaining'
    | 'ticketOptionsRange'
> &
    Pick<
        TicketQuantityPickerProps,
        'name' | 'onQuantityChange' | 'selectedQuantity' | 'ticketStatus'
    > &
    Pick<TicketSalesEndDateProps, 'formattedSalesEndDate' | 'hideSaleDates'>;

const TicketDisplayCardContentCompactSize: FunctionComponent<
    TicketDisplayCardContentCompactSizeProps
> = ({
    description,
    displayPrice,
    feeAndTax,
    formattedSalesEndDate,
    hideSaleDates,
    id,
    isDisabled,
    name,
    onQuantityChange,
    quantityRemaining,
    selectedQuantity,
    ticketOptionsRange,
    ticketStatus,
}) => {
    const [expanded, setExpanded] = useState(false);

    const extraInformation = expanded ? (
        <div id={`extra-information-${id}`}>
            <div
                data-testid="ticket-card-compact-size__extra-info"
                className="ticket-card-compact-size__extra-info"
            >
                <span
                    data-testid="ticket-card-compact-size-fee"
                    className="ticket-card-compact-size__fee eds-text-bs eds-text-color--ui-600"
                >
                    {feeAndTax.join(' / ')}
                </span>

                <TicketSalesEndDate
                    formattedSalesEndDate={formattedSalesEndDate}
                    ticketStatus={ticketStatus}
                    hideSaleDates={hideSaleDates}
                />
            </div>
            <div
                className="eds-text-bm ticket-card-compact-size__description eds-text-color--ui-600"
                data-testid="ticket-card-compact-size-description"
            >
                {description}
            </div>
        </div>
    ) : null;

    const _handleOnTicketToggle = () => {
        setExpanded(!expanded);
        logEvent(
            STATSIG_EVENT_NAMES.TICKET_SELECTION_COMPACT_STEP_MORE_INFO_CLICK,
        );
    };

    return (
        <div
            className="ticket-card-compact-size__container"
            data-testid="ticket-card-compact-size"
        >
            <div className="ticket-card-compact-size__title">
                <div
                    className="eds-text-bm eds-text-weight--heavy"
                    data-testid="ticket-card-compact-size-name"
                >
                    {name}
                </div>
                <TicketStepperSelector
                    variantId={id}
                    selectedQuantity={selectedQuantity}
                    isDisabled={isDisabled}
                    onQuantityChange={(newQuantity) => {
                        onQuantityChange(newQuantity);
                    }}
                    ticketOptionsRange={ticketOptionsRange}
                />
            </div>

            <div className="ticket-card-compact-size__price-container">
                <div
                    className="ticket-card-compact-size__price"
                    data-testid="ticket-card-compact-size-display-price"
                >
                    <span className="eds-text-bm eds-text-weight--heavy">
                        {displayPrice}
                    </span>
                    <button
                        data-testid="ticket-card-compact-size__info"
                        className={classNames(
                            'ticket-card-compact-size__info',
                            {
                                ['ticket-card-compact-size__info--active']:
                                    expanded,
                            },
                        )}
                        type="button"
                        aria-label={SHOW_MORE_INFO_BUTTON_LABEL.toString()}
                        aria-pressed={expanded}
                        aria-expanded={expanded}
                        aria-controls={`extra-information-${id}`}
                        onClick={_handleOnTicketToggle}
                    >
                        <Illustration
                            type={<InfoChunky fill="#2D49BE" />}
                            width="24px"
                            height="24px"
                        />
                    </button>
                </div>
                <TicketRemainingTicketsCount
                    numTicketsRemaining={quantityRemaining}
                    statusKey={ticketStatus}
                />
            </div>

            {extraInformation}
        </div>
    );
};

export default TicketDisplayCardContentCompactSize;
